import { reactive, ref, inject, computed } from "vue";
import { getJSON } from "@api/request";
import { getActivityApi } from "@utils";

const showCurErrorTips = ref();
const showSuccessTips = ref();

const physicalAwardPopupState = ref(false);
const physicalDoubleCheckPopupState = ref(false);

// 背包相关状态
const page = ref(1);
const awardLogList = ref<any[]>([]);
const total = ref(0);
const pageSize = 6;

// 实物奖励相关状态
const showToast = ref(false);

// 虚拟奖励兑换码
export const virtualAwardDetailPopupState = ref(false);
export const virtualAwardDetailCode = ref("");

// 常量定义
const TOAST_DISPLAY_DURATION = 1500; // Toast 显示时长（毫秒）
const MAX_NAME_LENGTH = 20;
const MAX_PHONE_LENGTH = 30;
const MAX_ADDRESS_LENGTH = 200;

const viewCode = async (item) => {
  const id = item.ID;
  const res = await getJSON(
    getActivityApi(
      window.platform_id,
      window.activity_name,
      "virtualAwardDetail"
    ),
    {
      params: JSON.stringify({
        id,
      }),
    }
  );
  if (res.result === 1 && res.data?.awardValue) {
    virtualAwardDetailCode.value = res.data.awardValue;
    virtualAwardDetailPopupState.value = true;
  } else {
    showCurErrorTips.value?.(res.code);
  }
};

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(total.value / pageSize) || 1;
});

// 计算要显示的页码
const displayPages = computed(() => {
  const pages: (number | string)[] = [];
  const totalPage = totalPages.value;

  if (totalPage <= 5) {
    // 总页数不超过5页，全部显示
    for (let i = 1; i <= totalPage; i++) {
      pages.push(i);
    }
  } else {
    // 总页数超过5页，显示前2页+省略号+后3页
    for (let i = 1; i <= 2; i++) {
      pages.push(i);
    }
    pages.push("...");
    for (let i = totalPage - 2; i <= totalPage; i++) {
      pages.push(i);
    }
  }

  return pages;
});

const fetchAwardList = async () => {
  let res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "myAward"),
    {
      params: JSON.stringify({
        page: page.value,
        pageSize: pageSize,
      }),
    }
  );

  if (res.result === 1) {
    awardLogList.value = res.data.awardData || [];
    total.value = res.data.total || 0;
  } else {
    showCurErrorTips.value?.(res.code);
  }

  return res;
};

// 切换页码
const switchPage = (pageNum: number) => {
  page.value = pageNum;
  fetchAwardList();
};

// 初始化背包列表
const initAwardList = async () => {
  awardLogList.value = [];
  page.value = 1;
  await fetchAwardList();
};

// 重置背包列表
const resetAwardList = () => {
  awardLogList.value = [];
  page.value = 1;
};

const physicalAwardUserState = reactive({
  phone: "",
  name: "",
  address: "",
  hasPhoneError: false,
  hasNameError: false,
  hasAddressError: false,
});

// 虚拟奖励
const getVirtualAward = async () => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "virtualAward"),
    {}
  );
  if (res.result === 1) {
    showSuccessTips.value?.("請至【我的獎品】頁面查看禮包碼");
    // 领奖成功后刷新背包列表
    await initAwardList();
    // 重新请求basic接口
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

const getPhysicalAward = async (params: {
  phone: string;
  name: string;
  address: string;
}) => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "materialAward"),
    {
      params: JSON.stringify(params),
    }
  );
  return res;
};

// 实物奖励
const fillPhysicalAward = () => {
  physicalAwardPopupState.value = true;
};

// 字段验证辅助函数
const validateField = (
  value: string,
  maxLength: number,
  errorStateKey: "hasNameError" | "hasPhoneError" | "hasAddressError"
): boolean => {
  const isValid = value && value.length > 0 && value.length <= maxLength;
  (physicalAwardUserState as any)[errorStateKey] = !isValid;
  return isValid;
};

const doubleCheckPhysicalAward = () => {
  const isNameValid = validateField(
    physicalAwardUserState.name,
    MAX_NAME_LENGTH,
    "hasNameError"
  );
  if (!isNameValid) return;

  const isPhoneValid = validateField(
    physicalAwardUserState.phone,
    MAX_PHONE_LENGTH,
    "hasPhoneError"
  );
  if (!isPhoneValid) return;

  const isAddressValid = validateField(
    physicalAwardUserState.address,
    MAX_ADDRESS_LENGTH,
    "hasAddressError"
  );
  if (!isAddressValid) return;

  physicalAwardPopupState.value = false;
  physicalDoubleCheckPopupState.value = true;
};

const cancelDoubleCheckPhysicalAward = () => {
  physicalDoubleCheckPopupState.value = false;
  physicalAwardPopupState.value = true;
};

const submitPhysicalAward = async () => {
  const res = await getPhysicalAward({
    phone: encodeURIComponent(physicalAwardUserState.phone),
    name: encodeURIComponent(physicalAwardUserState.name),
    address: encodeURIComponent(physicalAwardUserState.address),
  });
  // let res = {result: 1}
  if (res.result === 1) {
    showToast.value = true;
    setTimeout(async () => {
      showToast.value = false;
      physicalDoubleCheckPopupState.value = false;
      resetPhysicalAwardForm();
      // 领奖成功后刷新背包列表
      await initAwardList();
    }, TOAST_DISPLAY_DURATION);
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

// 重置实物奖励表单
const resetPhysicalAwardForm = () => {
  physicalAwardUserState.phone = "";
  physicalAwardUserState.name = "";
  physicalAwardUserState.address = "";
  physicalAwardUserState.hasPhoneError = false;
  physicalAwardUserState.hasNameError = false;
  physicalAwardUserState.hasAddressError = false;
};

export default function useGetAward(
  funcShowSuccessTips = null,
  funcShowCurErrorTips = null
) {
  if (funcShowSuccessTips) {
    showSuccessTips.value = funcShowSuccessTips;
  }
  if (funcShowCurErrorTips) {
    showCurErrorTips.value = funcShowCurErrorTips;
  }
  return {
    // 背包相关
    page,
    awardLogList,
    total,
    totalPages,
    displayPages,
    switchPage,
    initAwardList,
    resetAwardList,
    // 领奖相关
    physicalAwardPopupState,
    physicalDoubleCheckPopupState,
    physicalAwardUserState,
    fillPhysicalAward,
    doubleCheckPhysicalAward,
    cancelDoubleCheckPhysicalAward,
    submitPhysicalAward,
    resetPhysicalAwardForm,
    getVirtualAward,
    getPhysicalAward,
    showToast,
    virtualAwardDetailPopupState,
    virtualAwardDetailCode,
    viewCode,
  };
}
